<script setup lang="ts">
import {
  ROADS_URL,
  SATELLITE_URL,
  YMap,
  YMapDefaultFeaturesLayer,
  YMapDefaultSchemeLayer,
  YMapFeature,
  YMapLayer,
  YMapListener,
  YMapMarker,
  YMapTileDataSource,
} from '@/shared/lib'
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { VInventMapMarker, VTransformMarker } from '@/shared/ui'
import { VInventMapCluster } from '@/shared/ui/VInventMapCluster'
import { VInventMapInfoMarker } from '@/shared/ui/VInventMapInfoMarker'
import type { InventObject } from '@/widgets/InventoryMap/model/classes/InventObject.ts'
import type { InventoryMapCluster, InventoryMapTypesDict } from '@/widgets/InventoryMap'
import type {
  MapAdapterEntity,
  MapComponentExpose,
  MapLocation,
  YandexMapEntity,
} from '@/widgets/InventoryMap/model/adapters/types.ts'
import {
  INVENTORY_LINE_REMOVE_HIT_OPACITY,
  resolveInventoryLineRemoveHitWidth,
} from '@/widgets/InventoryMap/model/lineHitArea.ts'

type MapType = 'scheme' | 'satellite' | 'hybrid'
type OffsetMarkerEntry = { item: InventObject; pixelOffset: [number, number] }
type SpiderMemberEntry = { item: InventObject; origin: [number, number] }
type SpiderOriginEntry = { clusterId: string; coordinates: [number, number] }
type MapObjects = {
  features: InventObject[]
  markers: OffsetMarkerEntry[]
  selectedMarkers: OffsetMarkerEntry[]
  clusters: InventoryMapCluster[]
  stacks: InventoryMapCluster[]
  spiderMembers: SpiderMemberEntry[]
  spiderOrigins: SpiderOriginEntry[]
}

const DEFAULT_BEHAVIORS = [
  'drag',
  'pinchZoom',
  'scrollZoom',
  'dblClick',
  'magnifier',
  'oneFingerZoom',
  'mouseRotate',
  'mouseTilt',
  'pinchRotate',
  'panTilt',
] as const

type Props = {
  center: number[]
  zoom: number
  mapType: MapType
  pointerCount: number
  editFeature: any
  objects: MapObjects
  types: InventoryMapTypesDict
  selectedObjects: number[]
  action: string
  zoomSwitchValue: number
  zoomSmallMarker: number
  editSelectObject: any
  editNewPoint: any
  isEditNewPoint: boolean
  isPlaceMode: boolean
  placePointerType: any
  addObjectType: any
  pointerScreenCoordinates: number[]
  getFeatureStyle: (object: InventObject) => Record<string, any>
  canRelocateObject: (object: InventObject) => boolean
  objectMoveBlockedHandler: (object: InventObject) => void
  objectClickHandler: (object: InventObject, event: MouseEvent) => void
  objectEndDragHandler: (object: InventObject, coordinates: any) => void
  objectStartDragHandler: (object: InventObject) => void
  objectMoveDragHandler: (object: InventObject, coordinates: any) => void
  clusterSelectHandler: (cluster: InventoryMapCluster) => void
  collapseStackHandler: (clusterId: string) => void
  dragStartEditHandler: () => void
  dragMoveEditHandler: (index: number, coordinates: any) => void
  dragEndEditHandler: () => void
  clickEditHandler: (index: number, event: any) => void
  clickAddPointHandler: () => void
  updateCoordsHandler: () => void
  mapCenteredHandler: (coords: number[]) => void
  selectCoordsType: '' | 'square' | 'circle'
  isAreaSelectMapDragEnabled: boolean
  selectedHandler: (coords: any) => void
  updateHandler: (event: any) => void
  mouseMoveHandler: (target: any, event: any) => void
  clickHandler: (target: any, event: any) => void
  mouseUpHandler: (target: any, event: any) => void | Promise<void>
  mouseDownHandler: (target: any, event: any) => void
}

const props = defineProps<Props>()
const ymap = ref<{ entity?: YandexMapEntity }>()
const pendingLocation = ref<MapLocation | null>(null)
const baseBehaviors = ref<string[]>([...DEFAULT_BEHAVIORS])

const allMarkers = computed<OffsetMarkerEntry[]>(() => [
  ...props.objects.markers,
  ...props.objects.selectedMarkers,
])

const removableLineObjects = computed<InventObject[]>(() =>
  props.action === 'remove'
    ? (props.objects?.features ?? []).filter((item: InventObject) => item.isLine())
    : [],
)

/**
 * Создаёт невидимую непрерывную обводку поверх линии для удобного клика при удалении.
 */
const getLineRemoveHitStyle = (item: InventObject) => {
  const featureStyle = props.getFeatureStyle(item)
  const stroke = Array.isArray(featureStyle.stroke) ? featureStyle.stroke[0] : featureStyle.stroke

  return {
    stroke: [
      {
        color: '#000000',
        opacity: INVENTORY_LINE_REMOVE_HIT_OPACITY,
        width: resolveInventoryLineRemoveHitWidth(stroke?.width),
        dash: [],
      },
    ],
    fillOpacity: 0,
    interactive: true,
    cursor: 'pointer',
    zIndex: featureStyle.zIndex,
  }
}

const getYmapEntity = () => ymap.value?.entity
const normalizeBehaviors = (next?: string[]) =>
  Array.from(new Set(next?.length ? next : DEFAULT_BEHAVIORS))
const mapBehaviors = computed(() =>
  props.selectCoordsType && !props.isAreaSelectMapDragEnabled
    ? baseBehaviors.value.filter((item) => item !== 'drag')
    : [...baseBehaviors.value],
)

/**
 * Применяет действия, которые родитель мог вызвать до готовности внутреннего async YMap.
 * Это сохраняет прежний контракт `map.value.entity` для useModel.
 */
const flushPendingEntityActions = () => {
  const currentEntity = getYmapEntity()
  if (!currentEntity) return

  if (pendingLocation.value) {
    currentEntity.setLocation(pendingLocation.value)
    pendingLocation.value = null
  }

  currentEntity.setBehaviors(mapBehaviors.value)
}

const entity: MapAdapterEntity = {
  get center() {
    return getYmapEntity()?.center
  },
  get zoom() {
    return getYmapEntity()?.zoom
  },
  get bounds() {
    return getYmapEntity()?.bounds
  },
  get behaviors() {
    return [...mapBehaviors.value]
  },
  setLocation(location) {
    const currentEntity = getYmapEntity()
    if (!currentEntity) {
      pendingLocation.value = location
      return
    }

    currentEntity.setLocation(location)
  },
  getBounds() {
    return getYmapEntity()?.getBounds()
  },
  setBehaviors(next) {
    baseBehaviors.value = normalizeBehaviors(next)

    const currentEntity = getYmapEntity()
    if (!currentEntity) return

    currentEntity.setBehaviors(mapBehaviors.value)
  },
}

onMounted(async () => {
  await nextTick()
  flushPendingEntityActions()
})

watch(mapBehaviors, (next) => {
  const currentEntity = getYmapEntity()
  if (!currentEntity) return

  currentEntity.setBehaviors(next)
})

const updateYmapHandler = (event: any) => {
  flushPendingEntityActions()
  props.updateHandler(event)
}

/**
 * Yandex начинает drag раньше, чем приходит mouseDown listener.
 * В режиме area-select запрещаем drag без Shift, иначе карта едет вместе с рамкой.
 */
const actionStartHandler = (event: any) => {
  if (props.selectCoordsType && !props.isAreaSelectMapDragEnabled && event?.type === 'drag') {
    event.preventDefault?.()
  }
}

defineExpose<MapComponentExpose>({
  entity,
})
</script>

<template>
  <YMap
    class="w-invent-map__wrapper"
    :location="{ center, zoom }"
    :behaviors="mapBehaviors"
    ref="ymap"
  >
    <YMapDefaultSchemeLayer v-if="mapType === 'scheme'" />

    <template v-if="mapType === 'satellite' || mapType === 'hybrid'">
      <YMapTileDataSource
        id="satellite"
        :raster="{ type: 'ground', fetchTile: SATELLITE_URL }"
        :zoom-range="{ min: 0, max: 21 }"
      />
      <YMapLayer source="satellite" type="ground" :z-index="1000" />
    </template>

    <template v-if="mapType === 'hybrid'">
      <YMapTileDataSource
        id="roads"
        :raster="{ type: 'ground', fetchTile: ROADS_URL }"
        :zoom-range="{ min: 0, max: 21 }"
      />
      <YMapLayer source="roads" type="ground" :z-index="1001" />
    </template>

    <YMapDefaultFeaturesLayer />

    <!--  Фигура для рисования  -->
    <YMapFeature v-if="pointerCount" :geometry="editFeature.geometry" :style="editFeature.style" />

    <!--  Объекты на карте (линии/полигоны — сервер уже не свернул их в кластер)  -->
    <YMapFeature
      v-for="item in objects.features"
      :key="item.getId()"
      :geometry="item.getGeometry()"
      :style="getFeatureStyle(item)"
      :draggable="action === 'move' && canRelocateObject(item)"
      @click="(event: any) => objectClickHandler(item, event)"
      @dragEnd="(c: any) => objectEndDragHandler(item, c)"
      @dragStart="() => objectStartDragHandler(item)"
      @dragMove="(c: any) => objectMoveDragHandler(item, c)"
    />

    <!-- Невидимая hit-area не меняет внешний вид линии, но даёт небольшой допуск клика. -->
    <YMapFeature
      v-for="item in removableLineObjects"
      :key="`remove-hit-${item.getId()}`"
      :geometry="item.getGeometry()"
      :style="getLineRemoveHitStyle(item)"
      @click="(event: any) => objectClickHandler(item, event)"
    />

    <!-- Точки вьюпорта (не свёрнутые в кластер сервером). Смещение по кольцу
         применяется только к тем, кто реально совпал координатой (offset 0,0 иначе). -->
    <YMapMarker
      v-for="entry in allMarkers"
      :key="entry.item.getId()"
      :coordinates="entry.item.getMapCoords()"
      :draggable="action === 'move' && canRelocateObject(entry.item)"
      :zIndex="selectedObjects.includes(entry.item.getId()) ? 100 : 50"
      @click="(event: any) => objectClickHandler(entry.item, event)"
      @dragEnd="(c: any) => objectEndDragHandler(entry.item, c)"
      @dragStart="() => objectStartDragHandler(entry.item)"
      @dragMove="(c: any) => objectMoveDragHandler(entry.item, c)"
    >
      <div
        :style="{
          transform: `translate(${entry.pixelOffset[0]}px, ${entry.pixelOffset[1]}px)`,
        }"
      >
        <VInventMapMarker
          v-if="action === 'move'"
          :item="entry.item"
          :style="{ ...getFeatureStyle(entry.item) }"
          :small="zoom >= zoomSmallMarker && zoom <= zoomSwitchValue"
          :dot="zoom < zoomSmallMarker"
          @mousedown="objectMoveBlockedHandler(entry.item)"
        />
        <VInventMapInfoMarker
          v-else
          :item="entry.item"
          :selected="selectedObjects"
          :small="zoom >= zoomSmallMarker && zoom <= zoomSwitchValue"
          :dot="zoom < zoomSmallMarker"
        />
      </div>
    </YMapMarker>

    <!-- Кластеры и «стопки» (colocated) — как отдал сервер, без клиентской агрегации -->
    <YMapMarker
      v-for="cluster in [...objects.clusters, ...objects.stacks]"
      :key="cluster.id"
      :coordinates="cluster.coordinates"
    >
      <VInventMapCluster
        :counts-by-type="cluster.counts_by_type"
        :types="types"
        :colocated="cluster.colocated"
        :disabled="selectedObjects.length > 0"
        @selected="clusterSelectHandler(cluster)"
      />
    </YMapMarker>

    <!-- Раскрытая стопка: «ножка» от центра к каждому участнику, посчитанная сервером (`expand`) -->
    <YMapFeature
      v-for="entry in objects.spiderMembers"
      :key="`spider-leg-${entry.item.getId()}`"
      :geometry="{ type: 'LineString', coordinates: [entry.origin, entry.item.getMapCoords()] }"
      :style="{ stroke: [{ color: '#00000080', width: 1, dash: [] }] }"
    />

    <!-- Участники раскрытой стопки — обычные точечные маркеры по координатам от сервера -->
    <YMapMarker
      v-for="entry in objects.spiderMembers"
      :key="entry.item.getId()"
      :coordinates="entry.item.getMapCoords()"
      :zIndex="60"
      @click="(event: any) => objectClickHandler(entry.item, event)"
    >
      <VInventMapMarker
        v-if="action === 'move'"
        :item="entry.item"
        :style="{ ...getFeatureStyle(entry.item) }"
        @mousedown="objectMoveBlockedHandler(entry.item)"
      />
      <VInventMapInfoMarker v-else :item="entry.item" :selected="selectedObjects" />
    </YMapMarker>

    <!-- Центр раскрытой стопки — клик сворачивает обратно -->
    <YMapMarker
      v-for="origin in objects.spiderOrigins"
      :key="`spider-origin-${origin.clusterId}`"
      :coordinates="origin.coordinates"
      :zIndex="55"
      @click.stop="collapseStackHandler(origin.clusterId)"
    >
      <div class="w-invent-map__spider-origin" />
    </YMapMarker>

    <!--  Точки редактирования объекта -->
    <template v-if="editSelectObject">
      <YMapMarker
        v-for="(coords, index) in editSelectObject.getTransformCoords()"
        :draggable="true"
        :coordinates="coords"
        :key="index"
        @dragStart="dragStartEditHandler"
        @dragMove="(c: any) => dragMoveEditHandler(index, c)"
        @dragEnd="dragEndEditHandler"
        @click="(c: any) => clickEditHandler(index, c)"
      >
        <VTransformMarker />
      </YMapMarker>
    </template>

    <YMapMarker
      v-if="editNewPoint && isEditNewPoint"
      :coordinates="editNewPoint.geometry.coordinates"
      @click.stop="clickAddPointHandler"
    >
      <VTransformMarker is-optional />
    </YMapMarker>

    <YMapListener
      @actionStart="actionStartHandler"
      @update="updateYmapHandler"
      @mouseMove="(o: any, e: any) => mouseMoveHandler(o, e)"
      @click="(o: any, e: any) => clickHandler(o, e)"
      @mouseUp="(o: any, e: any) => mouseUpHandler(o, e)"
      @mouseDown="(o: any, e: any) => mouseDownHandler(o, e)"
    ></YMapListener>
  </YMap>
</template>
